import React, { useCallback, useMemo } from 'react';
import {
  arrayOf, func, bool, string, any, objectOf, oneOf, shape,
} from 'prop-types';
import {
  Transcript as TranscriptIcon, AiVideo, WhatsNew, Text as TextIcon, BookmarkIcon,
} from '@picsio/icons';
import { IconButton } from '@picsio/ui';
import { navigate, isRoutePreview } from '../../../../helpers/history';
import Logger from '../../../../services/Logger';
import picsioConfig from '../../../../../../../config';
import { calculateAiMinutes } from '../../../../helpers/calculateAICredits';
import { parseTime, LocalStorage } from '../../../../shared/utils';
import DetailsBlock from '../../../DetailsBlock';
import l18n from '../../../../shared/strings';
import { showDialog } from '../../../dialog';
import Toast from '../../../Toast/index';
import Placeholder from '../../../DetailsBlock/Placeholder';
import AiIcon from '../AiIcon';
import Tooltip from '../../../Tooltip';
import { LOCAL_STORAGE_VIEW_KEY } from '../../../TranscriptPanel/components/TranscriptList/TranscriptList';
import VIEW_OPTIONS from '../../../TranscriptPanel/components/ViewsSelector/options';
import Item from './Item';
import './styles.scss';

const LOCAL_STORAGE_HIDE_OVERWRITE_KEY = 'picsio.hideOverwriteVideoAudioAiDialog';

function Transcript({
  assetIds,
  assets,
  team,
  detailsPanelVisibility,
  toggleVisibility,
  blockTitle,
  blockName,
  isMainApp,
  transcribeAction,
  transcriptPermission,
  subscriptionFeatures,
  transcribing,
  transcribingErrorCode,
  videoAnalyzing,
  addonsFeatures,
}) {
  const defaultLanguage = team?.policies?.transcribingLanguage;

  const isMediaAsset = assets.length === 1 && (assets[0]?.isVideo || assets[0]?.isAudio);

  const canTranscribe = assetIds.length >= 2 || isMediaAsset;

  const transcriptPreview = useMemo(() => {
    /** if selected more than one asset or on the asset no transcripts */
    if (assets.length !== 1 || !assets[0]?.transcripts?.length) return null;
    const asset = assets[0];
    const { transcripts, headRevisionId } = asset;
    const t = transcripts.find(({ revisionId }) => revisionId === headRevisionId) || null;

    if (!t) return null;
    const { transcript } = t;
    if (transcript?.length) {
      const fullPreview = transcript.map(({ text }) => text || '').join(' ');
      return fullPreview.length > 150 ? `${fullPreview.slice(0, 147)} ...` : fullPreview;
    }
    return '';
  }, [assets]);

  const chaptersPreview = useMemo(() => {
    if (assets.length !== 1 || !assets[0]?.videoAi?.length) return null;
    const asset = assets[0];
    const { videoAi = [], headRevisionId } = asset;
    const t = videoAi.find(({ revisionId }) => revisionId === headRevisionId) || null;

    if (!t) return null;
    const { chapters } = t;
    if (chapters?.length) {
      const fullPreview = chapters.map(({ text }) => text || '').join(' | ');
      return fullPreview.length > 150 ? `${fullPreview.slice(0, 147)} ...` : fullPreview;
    }
    return '';
  }, [assets]);

  const actionsPreview = useMemo(() => {
    if (assets.length !== 1 || !assets[0]?.videoAi?.length) return null;
    const asset = assets[0];
    const { videoAi = [], headRevisionId } = asset;
    const t = videoAi.find(({ revisionId }) => revisionId === headRevisionId) || null;

    if (!t) return null;
    const { actions } = t;
    if (actions?.length) {
      const fullPreview = actions.map(({ text }) => text || '').join(' | ');
      return fullPreview.length > 150 ? `${fullPreview.slice(0, 147)} ...` : fullPreview;
    }
    return '';
  }, [assets]);

  const ocrPreview = useMemo(() => {
    if (assets.length !== 1 || !assets[0]?.videoAi?.length) return null;
    const asset = assets[0];
    const { videoAi = [], headRevisionId } = asset;
    const t = videoAi.find(({ revisionId }) => revisionId === headRevisionId) || null;

    if (!t) return null;
    const { ocr } = t;
    if (ocr?.length) {
      const fullPreview = ocr.map(({ text }) => text || '').join(' | ');
      return fullPreview.length > 150 ? `${fullPreview.slice(0, 147)} ...` : fullPreview;
    }
    return '';
  }, [assets]);

  const showTranscriptPreview = useCallback((view) => (e) => {
    e.preventDefault();
    if (picsioConfig.isMainApp) {
      try {
        const selectedView = VIEW_OPTIONS.find((option) => option.value === view);
        if (selectedView) {
          Logger.log('User', selectedView.amplitudeEventName, {
            assetId: assetIds[0],
            assetName: assets[0]?.name,
            initiation_location: 'details panel',
          });
        }
      } catch (error) {
        Logger.warn('Error logging VideoIntelligence[Entity]Selected event:', error);
      }

      if (view) LocalStorage.set(LOCAL_STORAGE_VIEW_KEY, view);

      if (isRoutePreview()) window.dispatchEvent(new Event('hotkeys:preview:showTranscript'));
      else navigate(`/preview/${assetIds[0]}#videoAi`);
    }
  }, [assetIds, assets]);

  const handleSubmit = useCallback(() => {
    if (isMainApp && assetIds.length) {
      const locale = defaultLanguage;
      try {
        const isBulk = assetIds.length > 1;
        Logger.log('User', 'InfoPanelVideoAnalysisClicked', {
          assets_count: assetIds.length,
          is_bulk: isBulk,
          assetId: isBulk ? null : assetIds[0],
          assetName: isBulk ? null : assets[0]?.name,
          language: locale,
        });
      } catch (error) {
        Logger.warn('Error logging InfoPanelVideoAnalysisClicked event:', error);
      }
      const toProcess = () => {
        if (assetIds.length >= 2) {
          showDialog({
            title: l18n.TRANSCRIPT.DIALOGS.titleBulkTranscribing,
            text: l18n.TRANSCRIPT.DIALOGS.textBulkTranscribing(assetIds.length),
            textBtnCancel: l18n.TRANSCRIPT.DIALOGS.btnCancelBulkTranscribing,
            textBtnOk: l18n.TRANSCRIPT.DIALOGS.btnCongirmBulkTranscribing,
            onOk: () => {
              transcribeAction({ assetIds, language: locale });
            },
          });
        } else {
          transcribeAction({ assetIds, language: locale });
        }
      };
      if (!LocalStorage.get(LOCAL_STORAGE_HIDE_OVERWRITE_KEY)
        && (transcriptPreview || chaptersPreview || ocrPreview || actionsPreview)
      ) {
        showDialog({
          title: l18n.TRANSCRIPT.DIALOGS.titleExistingTranscript,
          text: l18n.TRANSCRIPT.DIALOGS.textExistingTranscript(),
          textBtnCancel: l18n.TRANSCRIPT.DIALOGS.btnCancelBulkTranscribing,
          textBtnOk: l18n.TRANSCRIPT.DIALOGS.btnCongirmBulkTranscribing,
          checkbox: {
            label: l18n.DETAILS.labelCheckboxDontShow,
            onChange: (newValue) => {
              LocalStorage.set(LOCAL_STORAGE_HIDE_OVERWRITE_KEY, newValue);
            },
          },
          onOk: () => {
            Logger.log('User', 'RerunVideoAudioAnalysisModalConfirmClicked');
            toProcess();
          },
          onCancel: () => Logger.log('User', 'RerunVideoAudioAnalysisModalClosed'),
        });
        Logger.log('User', 'RerunVideoAudioAnalysisModalShown', {
          assetId: assets[0]?._id,
          assetName: assets[0]?.name,
          assets_size: assets[0]?.fileSize,
          asset_format: assets[0]?.mimeType,
        });
      } else {
        toProcess();
      }
    } else {
      Toast(l18n.TRANSCRIPT.TOASTS.textTranscribingHonSupported);
    }
  }, [isMainApp, assetIds, defaultLanguage, assets, transcriptPreview, chaptersPreview, ocrPreview, actionsPreview, transcribeAction]);

  const isTranscribing = useMemo(
    () => ['waiting', 'running'].includes(transcribing),
    [transcribing],
  );
  const isVideoAnalyzing = useMemo(
    () => ['waiting', 'running'].includes(videoAnalyzing),
    [videoAnalyzing],
  );

  const showPlaceholder = useMemo(() => {
    if (assetIds.length > 1) return true;
    if (isTranscribing || isVideoAnalyzing) return false;
    if (transcriptPreview || chaptersPreview || actionsPreview || ocrPreview) return false;
    return true;
  }, [actionsPreview, assetIds.length, chaptersPreview, isTranscribing, isVideoAnalyzing, ocrPreview, transcriptPreview]);

  const aiMinutes = useMemo(() => calculateAiMinutes(team), [team]);
  const aiMinutesString = parseTime(aiMinutes * 60, { type: 'hoursAndMinutes' });

  const actions = useMemo(
    () => {
      const result = [];
      if (assetIds.length === 1) {
        result.push(
          <Tooltip key="open-panel" content="View full details" placement="top">
            <IconButton
              onClick={showTranscriptPreview()}
              componentProps={{ 'data-testid': 'openTranscriptPanel' }}
              size="lg"
            >
              <AiVideo />
            </IconButton>
          </Tooltip>,
        );
      }

      let aiIconTooltip = !subscriptionFeatures || !aiMinutes
        ? l18n.DETAILS.aiIconUpdatePlanTooltipText(l18n.DETAILS.textVideoAnalysis)
        : l18n.DETAILS.aiIconMinutesTooltipText(l18n.DETAILS.textVideoAnalysis, aiMinutesString, 'text-center');
      if (!transcriptPermission) {
        aiIconTooltip = l18n.DETAILS.aiIconNoPermissionTooltipText();
      }
      result.push(
        <AiIcon
          key="ai-icon"
          data-testid="icongenerateTranscribe"
          size="lg"
          disabled={!subscriptionFeatures || !aiMinutes || isTranscribing || !transcriptPermission}
          inProgress={isTranscribing}
          tooltipText={aiIconTooltip}
          onSubmit={handleSubmit}
        />,
      );
      return result;
    },
    [assetIds, aiMinutes, aiMinutesString, handleSubmit, isTranscribing, showTranscriptPreview, subscriptionFeatures, transcriptPermission],
  );

  const aiPromptTexts = {
    transcript: 'Run AI analysis to generate transcript',
    chapters: addonsFeatures?.aiKit?.id ? 'Discover chapters with the new AI Kit' : 'Run AI analysis to generate chapters',
    actions: addonsFeatures?.aiKit?.id ? 'Discover actions with the new AI Kit' : 'Run AI analysis to identify actions',
    ocr: addonsFeatures?.aiKit?.id ? 'Discover OCR with the new AI Kit' : 'Run AI analysis to find visual text',
  };

  const emptyStateTexts = {
    transcript: 'No transcript detected',
    chapters: 'No chapters detected',
    actions: 'No actions detected',
    ocr: 'No visual text detected',
  };

  const placeholderTexts = {
    transcript: transcriptPreview === null ? aiPromptTexts.transcript : emptyStateTexts.transcript,
    chapters: chaptersPreview === null ? aiPromptTexts.chapters : emptyStateTexts.chapters,
    actions: actionsPreview === null ? aiPromptTexts.actions : emptyStateTexts.actions,
    ocr: ocrPreview === null ? aiPromptTexts.ocr : emptyStateTexts.ocr,
  };
  const isVideoAiError = videoAnalyzing === 'failed';

  /** Don't show block if selected only one asset AND it's not a videos or audio asset */
  if (!canTranscribe) return null;

  return (
    <DetailsBlock
      showAi={false}
      dataQa="transcript"
      detailsPanelVisibility={detailsPanelVisibility}
      toggleVisibility={toggleVisibility}
      blockName={blockName}
      blockTitle={blockTitle}
      showUpgradePlan={!subscriptionFeatures}
      badgeType="minutes"
      actions={actions}
    >
      <Choose>
        <When condition={showPlaceholder}>
          <Placeholder>
            <Choose>
              <When condition={assetIds.length > 1}>
                {l18n.DETAILS.placeholderMultipleSelection}
              </When>
              <Otherwise>
                {l18n.DETAILS.placeholderTranscribe}
              </Otherwise>
            </Choose>
          </Placeholder>
        </When>
        <Otherwise>
          <Item
            title="Transcript"
            text={transcriptPreview || placeholderTexts.transcript}
            isPlaceholder={!transcriptPreview}
            tooltipText="Click to see transcript"
            onClick={showTranscriptPreview('transcript')}
            isError={transcribing === 'failed'}
            errorCode={transcribingErrorCode}
            icon={TranscriptIcon}
            inProgress={isTranscribing}
          />
          <Item
            title="Chapters"
            text={chaptersPreview || placeholderTexts.chapters}
            isPlaceholder={!chaptersPreview}
            tooltipText="Click to see chapters"
            onClick={showTranscriptPreview('chapters')}
            isError={isVideoAiError && !chaptersPreview}
            icon={BookmarkIcon}
            inProgress={isVideoAnalyzing}
          />
          <Item
            title="Actions"
            text={actionsPreview || placeholderTexts.actions}
            isPlaceholder={!actionsPreview}
            tooltipText="Click to see actions"
            onClick={showTranscriptPreview('actions')}
            isError={isVideoAiError && !actionsPreview}
            icon={WhatsNew}
            inProgress={isVideoAnalyzing}
          />
          <Item
            title="Recognized text (OCR)"
            text={ocrPreview || placeholderTexts.ocr}
            isPlaceholder={!ocrPreview}
            tooltipText="Click to see OCR"
            onClick={showTranscriptPreview('ocr')}
            isError={isVideoAiError && !ocrPreview}
            icon={TextIcon}
            inProgress={isVideoAnalyzing}
          />
        </Otherwise>
      </Choose>
    </DetailsBlock>
  );
}

Transcript.defaultProps = {
  detailsPanelVisibility: {},
  isMainApp: true,
  transcriptPermission: false,
  transcribeAction: Function.prototype,
  subscriptionFeatures: false,
  transcribing: 'skipped',
  transcribingErrorCode: null,
  videoAnalyzing: 'skipped',
  assets: [],
  addonsFeatures: {},
};

Transcript.propTypes = {
  assetIds: arrayOf(string).isRequired,
  addonsFeatures: shape({
    aiKit: shape({
      id: string,
    }),
  }),
  team: objectOf(any).isRequired,
  detailsPanelVisibility: objectOf(any),
  toggleVisibility: func.isRequired,
  blockTitle: string.isRequired,
  blockName: string.isRequired,
  isMainApp: bool,
  transcriptPermission: bool,
  transcribeAction: func,
  subscriptionFeatures: bool,
  transcribing: oneOf(['skipped', 'waiting', 'running', 'complete', 'failed', 'rejected']),
  transcribingErrorCode: string,
  videoAnalyzing: oneOf(['skipped', 'waiting', 'running', 'complete', 'failed', 'rejected']),
  assets: arrayOf(shape({
    headRevisionId: string,
    transcripts: arrayOf(shape({
      language: string,
      revisionId: string,
      transcript: shape({
        start: string,
        end: string,
        text: string,
      }),
    })),
  })),
};

export default Transcript;
